import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./styles.css";
import "./styles/landing.css";
import "./components/dashboard/Dashboard.css";
import { ThemeProvider } from "./components/dashboard/ThemeContext";
import { getAuthToken, getCurrentUser } from "./api/config";
import { logger } from "./utils/logger";

// 主页组件
import LandingPage from "./pages/LandingPage";
import ErrorBoundary from './components/ErrorBoundary';

// 引入Dashboard组件
import DashboardLayout from './components/dashboard/DashboardLayout';
import DashboardHome from './components/dashboard/DashboardHome';
import CloakList from './components/dashboard/cloak/CloakList';
import CloakLogs from './components/dashboard/cloak/CloakLogs';
import CloakPlans from './components/dashboard/cloak/CloakPlans';
import CloakPricing from './components/dashboard/cloak/CloakPricing';
import DashboardAuth from './components/dashboard/DashboardAuth';
import ApiInterceptor from './components/dashboard/ApiInterceptor';
import Loading from './components/Loading';

// 引入管理员组件
const AdminUserManagement = React.lazy(() => import('./components/dashboard/admin/UserManagement'));
const AdminPaymentReview = React.lazy(() => import('./components/dashboard/admin/PaymentReview'));
const AdminPlanManagement = React.lazy(() => import('./components/dashboard/admin/PlanManagement'));
const AdminDashboard = React.lazy(() => import('./components/dashboard/admin/AdminDashboard'));

// 主页组件
const HomePage = () => {
  return <LandingPage />;
};

// 路由守卫组件，用于检查用户是否已登录
const ProtectedRoute = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    logger.log("[ProtectedRoute] 认证检查 - JWT Token:", token ? `存在 (${token.substring(0, 10)}...)` : "不存在");
    setAuthenticated(!!token);
    setReady(true);
  }, []);

  logger.log("[ProtectedRoute] 用户认证状态:", authenticated ? "已登录" : "未登录");

  if (!ready) return null;
  // 将未登录用户重定向到登录页面，而不是主页
  return authenticated ? children : <Navigate to="/dashboard/login" />;
};

// 管理员权限检查组件
const AdminRoute = ({ children }) => {
  const [admin, setAdmin] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setAdmin(false);
    } else {
      const user = getCurrentUser();
      setAdmin(user && (user.role === 'admin' || user.role === 'superadmin'));
    }
    setReady(true);
  }, []);

  logger.log("[AdminRoute] 用户管理员状态:", admin ? "是管理员" : "非管理员");

  if (!ready) return null;
  // 非管理员用户重定向到仪表板主页
  return admin ? children : <Navigate to="/dashboard" />;
};

// 已登录用户访问登录页面时重定向到后台
const RedirectIfAuthenticated = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    logger.log("[RedirectIfAuthenticated] 认证检查 - JWT Token:", token ? `存在 (${token.substring(0, 10)}...)` : "不存在");

    if (!token) {
      setAuthenticated(false);
      setIsAdmin(false);
    } else {
      const currentUser = getCurrentUser();
      const admin = currentUser && (currentUser.role === 'admin' || currentUser.role === 'superadmin');
      setAuthenticated(true);
      setIsAdmin(admin);
      logger.log("[RedirectIfAuthenticated] 用户角色:", admin ? "管理员" : "普通用户");
    }
    setReady(true);
  }, []);

  logger.log("[RedirectIfAuthenticated] 用户认证状态:", authenticated ? "已登录" : "未登录");

  if (!ready) return null;

  if (authenticated) {
    // 根据用户角色重定向到相应的仪表盘
    if (isAdmin) {
      return <Navigate to="/dashboard/admin/dashboard" />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  }

  return children;
};

// 根组件
const App = () => {
  return (
    <ApiInterceptor>
    <Routes>
      {/* 主站路由 */}
      <Route path="/" element={<HomePage />} />
      
      {/* 登录/注册路由 */}
      <Route path="/dashboard/login" element={
        <RedirectIfAuthenticated>
          <DashboardAuth initialMode="login" />
        </RedirectIfAuthenticated>
      } />
      <Route path="/dashboard/register" element={
        <RedirectIfAuthenticated>
          <DashboardAuth initialMode="register" />
        </RedirectIfAuthenticated>
      } />
      
      {/* 后台路由 */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="cloak/list" element={<CloakList />} />
        <Route path="cloak/logs" element={<CloakLogs />} />
        <Route path="cloak/plans" element={<CloakPlans />} />
        <Route path="cloak/pricing" element={<CloakPricing />} />
        
        {/* 管理员路由 */}
        <Route path="admin/users" element={
          <AdminRoute>
            <React.Suspense fallback={<Loading />}>
              <AdminUserManagement />
            </React.Suspense>
          </AdminRoute>
        } />
        <Route path="admin/payments" element={
          <AdminRoute>
            <React.Suspense fallback={<Loading />}>
              <AdminPaymentReview />
            </React.Suspense>
          </AdminRoute>
        } />
        <Route path="admin/plans" element={
          <AdminRoute>
            <React.Suspense fallback={<Loading />}>
              <AdminPlanManagement />
            </React.Suspense>
          </AdminRoute>
        } />
        <Route path="admin/dashboard" element={
          <AdminRoute>
            <React.Suspense fallback={<Loading />}>
              <AdminDashboard />
            </React.Suspense>
          </AdminRoute>
        } />
      </Route>
      
      {/* 404 路由 */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </ApiInterceptor>
  );
};

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error("未找到id为'root'的DOM元素");

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
); 